/**
 * accountContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";
import { Route, Routes, Link, Navigate, useNavigate } from "react-router-dom";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import UILink from "@mui/material/Link";

import { MOBILE_CUTOFF, ClassToggles } from "../models/constants";

import { AccountHeader } from "../headers/accountHeader.component";
import { InviteMemberContainer } from "./inviteMember.component";
import { LeadingSideInner } from "../components/leadingSideInner.component";
import { AuthContext } from "../auth/auth.context";
import { AccountNavContext } from "./account.context";
import { AccountChooser } from "./accountChooser.component";
import { AccountGroupContainer } from "./accountGroup.component";
import { AccountGroupListContainer } from "./accountGroupList.component";
import { AccountInfoContainer } from "./accountInfo.component";
import { AccountMemberContainer } from "./accountMember.component";
import { AccountMemberListContainer } from "./accountMemberList.component";
import { AccountInvitesListContainer } from "./accountInvitesList.component";
import { Account, Membership, PermissionLevel } from "../models/auth";
import { MeetingListContainer } from "../streaming/meetingList.component";
import { authServiceFactory } from "../services/auth.service";
import { AccountTeamsContainer } from "../profile/acctTeamsContainer.component";

export function AccountContainer(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const mainScrollRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  //const location = useLocation();

  const [account, setAccount] = React.useState<Account | null>(null);
  const [classes, setClasses] = React.useState<ClassToggles>({
    chatContainer: false,
    showLeftNav: window.innerWidth > MOBILE_CUTOFF,
    showRightDetail: false,
    multipleAccounts: false,
    withHuddle: false,
    hideChatInput: false
  });

  React.useEffect(function () {
    setClasses({
      ...classes,
      multipleAccounts: authContext.memberships.length > 1
    });
  }, [authContext.memberships]);

  React.useEffect(function () {
    if (authContext.membership && authContext.can(PermissionLevel.WRITE, "profiles")) {
      authServiceFactory().getAccountInfo(authContext.membership.accountID).then(setAccount)
        .catch(err => {
          // TODO: any additional error handling
          console.error("Error getting account info", (err as Error).message);
        });
    } else if (!authContext.can(PermissionLevel.WRITE, "profiles")) {
      navigate(-1);
    }
  }, [authContext.membership]);

  function toggleLeftNav(): void {
    setClasses({
      ...classes,
      showLeftNav: !classes.showLeftNav
    });
  }

  function hideLeftNav(): void {
    setClasses({ ...classes, showLeftNav: false });
  }

  function updateMembership(userID: string, update: Partial<Membership>): Promise<Membership> {
    if (update.isComp && !account?.remainingFreeMemberships) {
      console.error("No complementary memberships available for selected membership");
      update.isComp = undefined;
    }
    return authServiceFactory().updateMembership(userID, update).then((membership) => {
      if (update.isComp && membership.isComp) {
        updateCompCount(-1);
      } else if (update.isComp === false) {
        updateCompCount(1)
      }
      return membership;
    });
  }

  function updateCompCount(increment: number): void {
    setAccount((account) => {
      if (!account) { return account; }
      if (!account.allowedFreeMemberships || account.remainingFreeMemberships == undefined) { 
        console.error("No complementary slots are available for this account")
        return account; 
      }

      const newCount = account.remainingFreeMemberships + increment;
      if (newCount > account.allowedFreeMemberships) {
        console.error("Attempted to use more complementary slots than are available for this account");
        return account; 
      }
      if (newCount < 0) {
        console.error("Available slots can't be negative");
        return account;
      }
      account.remainingFreeMemberships = newCount;
      return account;
    })
  }

  const classNames = Object.keys(classes)
    .filter(cls => classes[cls as keyof ClassToggles])
    .join(" ");

  const context: AccountNavContext = {
    toggleLeftNav: toggleLeftNav,
    currentAccount: account,
  };

  return <AccountNavContext.Provider value={context}>
    <div className={classNames}>
      <div className="background-overlay" onClick={hideLeftNav} />
      <nav className="left-nav">
        <LeadingSideInner>
          <div className="choosers-container">
            <AccountChooser />
            <div className="account-container">
              <header className="iris-header">
                <img className="logo-img" src="/img/mercury-workspace.png" />
              </header>
              <AccountHeader hideMenu={true} />
              <div className="groups-list-container">
                <div className="nav-list-item">
                  <UILink variant="h3" className="back-link" component={Link} to="/group/" underline="none">
                    <ArrowBackIosNewIcon />
                    <span>Back to Chat</span>
                  </UILink>
                </div>
                {authContext.can(PermissionLevel.READ, "groups") && <div className="nav-list-item">
                  <UILink variant="h3" component={Link} to="/admin/groups/" underline="none">
                    Groups
                  </UILink>
                </div>}
                {authContext.can(PermissionLevel.READ, "profiles") && <div className="nav-list-item">
                  <UILink variant="h3" component={Link} to="/admin/members/" underline="none">
                    Members
                  </UILink>
                </div>}
                {authContext.can(PermissionLevel.WRITE, "profiles") && <div className="nav-list-item">
                  <UILink variant="h3" component={Link} to="/admin/invites/" underline="none">
                    Pending Invites
                  </UILink>
                </div>}
                {authContext.can(PermissionLevel.READ, "archive") && <div className="nav-list-item">
                  <UILink variant="h3" component={Link} to="/admin/" underline="none">
                    Archiving
                  </UILink>
                </div>}
                {authContext.can(PermissionLevel.WRITE, "meetings") && <div className="nav-list-item">
                  <UILink variant="h3" component={Link} to="/admin/meetings/" underline="none">
                    Meetings
                  </UILink>
                </div>}
              </div>
            </div>
          </div>
        </LeadingSideInner>
      </nav>
      <div className="both-side-margin main-section-container">
        <div ref={mainScrollRef} className="main-scroll-container">
          <Routes>
            {authContext.can(PermissionLevel.WRITE, "profiles") ?
              [
                <Route key="group" path="group/:groupID" element={<AccountGroupContainer />} />,
                <Route key="groups" path="groups" element={<AccountGroupListContainer />} />,
                <Route key="invite" path="invite" element={<InviteMemberContainer />} />,
                <Route key="members" path="members" element={<AccountMemberListContainer updateMembership={updateMembership} />} />,
                <Route key="member" path="member/:userID" element={<AccountMemberContainer />} />,
                <Route key="invites" path="invites" element={<AccountInvitesListContainer />} />,
                <Route key="meetings" path="meetings" element={<MeetingListContainer />} />,
                <Route key="teams" path="/teams" element={<AccountTeamsContainer />} />,

                <Route key="info" path="/" element={<AccountInfoContainer />} />,
                <Route key="default" element={<Navigate to="members" />} />
              ] : <Route element={<Navigate to="/group" />} />
            }
          </Routes>
        </div>
      </div>

    </div>
  </AccountNavContext.Provider>;
  // <Route path={`${this.props.match.path}/member/:memberID`} component={ChatContainer} />
}
